'use client';

import type { ReactNode } from 'react';
import { MarketRowSearchItem } from './market-row-search-item';
import { OutcomeRow } from './outcome-row';
import { ProposalRowSearchItem } from './proposal-row-search-item';
import type { BlendedSearchResult, CreatorSearchResult, MarketSearchResult, OutcomeSearchResult, ProposalSearchResult, UserSearchResult, WinnerSearchResult } from './search-types';
import { UserSearchRow } from './user-search-row';
import { WinnerRow } from './winner-row';
export function winnerHref(winner: WinnerSearchResult): string {
  return `/winners/${winner.mint}`;
}
function userResultKey(result: UserSearchResult, idx: number): string {
  const {
    userId,
    walletAddress
  } = result.user;
  if (userId !== null && userId.length > 0) return userId;
  if (walletAddress.length > 0) return walletAddress;
  return `user-${idx}`;
}

// Shared row context threaded through the row-list components below. Extracting
// these from the inline `{renderXRows()}` calls keeps each list a stable sibling
// subtree (no remount/state-loss as the parent re-renders).
export interface RowSharedProps {
  tickerHighlighted: boolean;
  onCommit: () => void;
  onHoverMarket: (pubkey: string) => void;
  onHoverWinner: (mint: string) => void;
  onHoverProposal: (pubkey: string) => void;
  onHoverUser: (identifier: string) => void;
}
export function buildMarketRow(m: Omit<MarketSearchResult, 'marketConfig'> & {
  marketConfig: string | undefined;
}, shared: Pick<RowSharedProps, 'tickerHighlighted' | 'onCommit' | 'onHoverMarket'>, exact: boolean = false): React.ReactNode {
  return <MarketRowSearchItem marketType={m.marketType} partnerCreatorId={m.partnerCreatorId} associatedCreator={m.associatedCreator} key={m.pubkey} pubkey={m.pubkey} title={m.title} coverImageUrl={m.coverImageUrl} coverImageNsfw={m.coverImageNsfw} status={m.status} marketConfig={m.marketConfig} volumeTotal={m.volumeTotal} prizePool={m.prizePool} numOutcomes={m.numOutcomes} ticker={m.ticker} tickerHighlighted={shared.tickerHighlighted} exact={exact} href={`/markets/${m.pubkey}`} onCommit={shared.onCommit} onHoverPrefetch={shared.onHoverMarket} />;
}
export function buildProposalRow(p: Omit<ProposalSearchResult, 'marketConfig'> & {
  marketConfig: string | undefined;
}, shared: Pick<RowSharedProps, 'tickerHighlighted' | 'onCommit' | 'onHoverProposal'>): React.ReactNode {
  return <ProposalRowSearchItem marketType={p.marketType} partnerCreatorId={p.partnerCreatorId} associatedCreator={p.associatedCreator} key={p.proposalId} proposalId={p.proposalId} title={p.title} coverImageUrl={p.coverImageUrl} coverImageNsfw={p.coverImageNsfw} totalFunded={p.totalFunded} marketConfig={p.marketConfig} numOutcomes={p.numOutcomes} backerCount={p.backerCount} ticker={p.ticker} tickerHighlighted={shared.tickerHighlighted} href={`/proposals/${p.proposalId}`} onCommit={shared.onCommit} onHoverPrefetch={shared.onHoverProposal} />;
}
export function buildUserRow(entry: UserSearchResult, idx: number, shared: Pick<RowSharedProps, 'onCommit' | 'onHoverUser'>): React.ReactNode {
  const u = entry.user;
  const portfolioId = u.userId ?? u.walletAddress;
  const key = userResultKey(entry, idx);
  return <UserSearchRow key={key} variant="user" rowKey={key} identifier={portfolioId} walletAddress={u.walletAddress} userId={u.userId} displayName={u.displayName} handle={u.handle} avatarUrl={u.avatarUrl} avatarNsfw={u.avatarNsfw === true} avatarPreferences={u.avatarPreferences ?? null} totalVolume={entry.totalVolume} totalTrades={entry.totalTrades} totalPnl={entry.totalPnl} partner={entry.partner} href={`/portfolio/${portfolioId}`} onCommit={shared.onCommit} onHoverPrefetch={shared.onHoverUser} />;
}
export interface UserRowsListProps {
  users: ReadonlyArray<UserSearchResult>;
  onCommit: () => void;
  onHoverUser: (identifier: string) => void;
}
export function UserRowsList({
  users,
  onCommit,
  onHoverUser
}: UserRowsListProps): ReactNode {
  return users.map((entry, idx) => buildUserRow(entry, idx, {
    onCommit,
    onHoverUser
  }));
}
export interface MarketRowsListProps {
  markets: ReadonlyArray<MarketSearchResult>;
  tickerHighlighted: boolean;
  onCommit: () => void;
  onHoverMarket: (pubkey: string) => void;
}
export function MarketRowsList({
  markets,
  tickerHighlighted,
  onCommit,
  onHoverMarket
}: MarketRowsListProps): ReactNode {
  return markets.map(m => buildMarketRow(m, {
    tickerHighlighted,
    onCommit,
    onHoverMarket
  }));
}
export interface ProposalRowsListProps {
  proposals: ReadonlyArray<ProposalSearchResult>;
  tickerHighlighted: boolean;
  onCommit: () => void;
  onHoverProposal: (pubkey: string) => void;
}
export function ProposalRowsList({
  proposals,
  tickerHighlighted,
  onCommit,
  onHoverProposal
}: ProposalRowsListProps): ReactNode {
  return proposals.map(p => buildProposalRow(p, {
    tickerHighlighted,
    onCommit,
    onHoverProposal
  }));
}
export interface OutcomeRowsListProps {
  outcomes: ReadonlyArray<OutcomeSearchResult>;
  tickerHighlighted: boolean;
  onCommit: () => void;
  onHoverMarket: (pubkey: string) => void;
}
export function OutcomeRowsList({
  outcomes,
  tickerHighlighted,
  onCommit,
  onHoverMarket
}: OutcomeRowsListProps): ReactNode {
  return outcomes.map(outcome => <OutcomeRow key={outcome.pubkey} outcome={outcome} tickerHighlighted={tickerHighlighted} href={`/markets/${outcome.market}?outcome=${encodeURIComponent(outcome.label)}`} onCommit={onCommit} onHoverPrefetch={onHoverMarket} />);
}
export interface WinnerRowsListProps {
  winners: ReadonlyArray<WinnerSearchResult>;
  tickerHighlighted: boolean;
  onCommit: () => void;
  onHoverWinner: (mint: string) => void;
}
export function WinnerRowsList({
  winners,
  tickerHighlighted,
  onCommit,
  onHoverWinner
}: WinnerRowsListProps): ReactNode {
  return winners.map(winner => <WinnerRow key={winner.pubkey} winner={winner} tickerHighlighted={tickerHighlighted} href={winnerHref(winner)} onCommit={onCommit} onHoverPrefetch={onHoverWinner} />);
}
export interface CreatorRowsListProps {
  creators: ReadonlyArray<CreatorSearchResult>;
  onCommit: () => void;
  onHoverUser: (identifier: string) => void;
}
export function CreatorRowsList({
  creators,
  onCommit,
  onHoverUser
}: CreatorRowsListProps): ReactNode {
  return creators.map(entry => {
    const creatorIdentifier = entry.creator.userId ?? entry.creator.walletAddress;
    return <UserSearchRow key={entry.creator.walletAddress} variant="creator" rowKey={entry.creator.walletAddress} identifier={creatorIdentifier} walletAddress={entry.creator.walletAddress} userId={entry.creator.userId ?? null} displayName={entry.creator.displayName ?? null} handle={entry.creator.handle ?? null} avatarUrl={entry.creator.avatarUrl ?? null} avatarNsfw={entry.creator.avatarNsfw === true} avatarPreferences={entry.creator.avatarPreferences ?? null} totalMarketsCreated={entry.totalMarketsCreated} href={`/portfolio/${entry.creator.walletAddress}`} onCommit={onCommit} onHoverPrefetch={onHoverUser} />;
  });
}
export interface BlendedRowsListProps {
  blended: ReadonlyArray<BlendedSearchResult>;
  marketByPubkey: Map<string, MarketSearchResult>;
  proposalById: Map<string, ProposalSearchResult>;
  tickerHighlighted: boolean;
  onCommit: () => void;
  onHoverMarket: (pubkey: string) => void;
  onHoverProposal: (pubkey: string) => void;
}
export function BlendedRowsList({
  blended,
  marketByPubkey,
  proposalById,
  tickerHighlighted,
  onCommit,
  onHoverMarket,
  onHoverProposal
}: BlendedRowsListProps): ReactNode {
  const marketShared = {
    tickerHighlighted,
    onCommit,
    onHoverMarket
  };
  const proposalShared = {
    tickerHighlighted,
    onCommit,
    onHoverProposal
  };
  return blended.map(item => {
    if (item.resultType === 'market') {
      const enriched = marketByPubkey.get(item.id);
      if (enriched !== undefined) return buildMarketRow(enriched, marketShared);
      // Fallback when the typed lookup misses (rare sort drift).
      return buildMarketRow({
        pubkey: item.id,
        title: item.title,
        category: item.category,
        status: item.status,
        volumeTotal: '0',
        coverImageUrl: item.coverImageUrl,
        coverImageNsfw: item.coverImageNsfw,
        similarity: item.similarity,
        prizePool: '0',
        marketConfig: undefined,
        ticker: '',
        numOutcomes: 0
      }, marketShared);
    }
    const enriched = proposalById.get(item.id);
    if (enriched !== undefined) return buildProposalRow(enriched, proposalShared);
    return buildProposalRow({
      proposalId: item.id,
      title: item.title,
      category: item.category,
      status: item.status,
      totalFunded: '0',
      coverImageUrl: item.coverImageUrl,
      coverImageNsfw: item.coverImageNsfw,
      similarity: item.similarity,
      ticker: '',
      marketConfig: undefined,
      numOutcomes: 0,
      backerCount: 0
    }, proposalShared);
  });
}
export type TrendingMarket = {
  pubkey: string;
  title: string;
  status: string;
  coverImageUrl: string;
  coverImageNsfw?: boolean;
  volumeTotal: string;
  prizePool: string;
  marketConfig: string;
  ticker?: string;
  outcomeCount?: number;
};
export function trendingRow(market: TrendingMarket, shared: RowSharedProps): React.ReactNode {
  return buildMarketRow({
    pubkey: market.pubkey,
    title: market.title,
    category: '',
    status: market.status,
    volumeTotal: market.volumeTotal,
    coverImageUrl: market.coverImageUrl,
    coverImageNsfw: market.coverImageNsfw === true,
    similarity: 0,
    prizePool: market.prizePool,
    marketConfig: market.marketConfig,
    ticker: market.ticker ?? '',
    numOutcomes: market.outcomeCount ?? 0
  }, shared);
}